import JSONApiSerializer from "json-api-serializer";
import { QuestionTypes } from "../static/enums/Questions";
import { Nullable } from "../@typings/typings";
import { UnionParser } from "../helpers/answers/UnionParser";

const Serializer = new JSONApiSerializer();

Serializer.register("topics", { id: "id" });
Serializer.register("skills", { id: "id" });
Serializer.register("tex_helpers", { id: "id" });
Serializer.register("parent-question", { id: "id" });
Serializer.register("questions", {
	id: "id",
	convertCase: "snake_case",
	relationships: {
		skills: {
			type: "skills",
		},
		topics: {
			type: "topics",
		},
		tex_helpers: {
			type: "tex_helpers",
		},
		parent_question: {
			type: "questions",
		},
	},
	blacklist: ["children_questions"],
	afterDeserialize: (rawData) => {
		// todo it's not IQuestionForm, we need to change it to JSONApi
		const data = rawData as any;

		if (data.parent_question) {
			data.parent_question = data.parent_question.id ? data.parent_question.id : data.parent_question;
		}

		if (data.children_questions) {
			data.children_questions = data.children_questions.sort((a: any, b: any) => a.order - b.order);
		}

		switch (data.class) {
			case QuestionTypes.choice_text_single: {
				const parsedConfig = data.answer_config! as unknown as Record<string, { label: string }>;
				data.answer_config = Object.values(parsedConfig) as any;
				break;
			}
			case QuestionTypes.choice_text_multiple: {
				const parsedData = data.answer_config! as unknown as Record<string, { label: string }>;
				data.answer_config = Object.values(parsedData) as any;
				const parsedAnswer = data.correct_answer! as unknown as Record<string, number>[];
				data.correct_answer = parsedAnswer.map((answer) => ({
					answer: Object.entries(answer)
						.filter(([_, value]) => !!value)
						.map(([key]) => key)
						.join(","),
				})) as any;
				break;
			}
			case QuestionTypes.input_table: {
				data.correct_answer = Object.values(data.correct_answer as unknown as Record<string, string>).reduce<
					Nullable<string>[]
				>((acc, v, i) => {
					acc[i] = v;
					return acc;
				}, []) as any;
				data.answer_config = Object.entries(
					data.answer_config as unknown as Record<number, { label: string; xpos: string; ypos: string }>
				).reduce<Nullable<{ label: string; xpos: string; ypos: string }>[]>((acc, [index, value]) => {
					acc[Number(index) - 1] = { ...value };
					return acc;
				}, []) as any;
				break;
			}
			case QuestionTypes.input_unions_systems: {
				data.correct_answer = UnionParser.parseAnswer(data.answer_config, data.correct_answer);
				data.answer_config = null;
			}
		}
		return data;
	},
	beforeSerialize: (rawData) => {
		const data = rawData as any;
		if (data.class) {
			data.number = Number(data.number);
			data.hint = data.hint ? data.hint : null;
		}

		switch (data.class) {
			case QuestionTypes.input_text: {
				data.answer_config = null;
				break;
			}
			case QuestionTypes.choice_text_single: {
				data.answer_config = (data.answer_config! as unknown as { label: string }[]).reduce<
					Record<number, { label: string }>
				>((acc, config, i) => {
					acc[i + 1] = { label: config.label };
					return acc;
				}, {}) as any;
				data.correct_answer = (data.correct_answer! as unknown as Nullable<string>[]).filter((num) => !!num) as any;
				break;
			}
			case QuestionTypes.choice_text_multiple: {
				const answersCount = data.answer_config!.length;
				data.answer_config = (data.answer_config! as unknown as { label: string }[]).reduce<
					Record<number, { label: string }>
				>((acc, config, i) => {
					acc[i + 1] = { label: config.label };
					return acc;
				}, {}) as any;
				data.correct_answer = (data.correct_answer! as unknown as Record<"answer", string>[]).map((answer) =>
					new Array(answersCount).fill(undefined).reduce((acc, _, i) => {
						acc[i + 1] = answer.answer.split(",")!.includes(`${i + 1}`) ? 1 : 0;
						return acc;
					}, {})
				) as any;
				break;
			}
			case QuestionTypes.input_table: {
				data.answer_config = (data.answer_config as unknown as { label: string; xpos: number; ypos: number }[]).reduce<
					Record<string, { label: string; xpos: number; ypos: number }>
				>((acc, x, i) => {
					if (data.correct_answer![i]) {
						acc[i + 1] = { label: String(x.label), xpos: x.xpos, ypos: x.ypos };
					}
					return acc;
				}, {}) as any;
				data.correct_answer = (data.correct_answer as string[]).reduce<Record<string, string>>((acc, x, i) => {
					if (x) {
						acc[i + 1] = String(x);
					}
					return acc;
				}, {}) as any;
				break;
			}
			case QuestionTypes.input_unions_systems: {
				const parsedTex = UnionParser.parseText(data.correct_answer);
				data.answer_config = parsedTex.reduce<Record<string, { label: string; path: string }>>((acc, answer, i) => {
					acc[i + 1] = {
						path: Object.keys(answer)[0],
						label: `#${i + 1}`,
					};
					return acc;
				}, {});
				data.correct_answer = parsedTex.reduce<Record<string, string>>((acc, answer, i) => {
					acc[i + 1] = Object.values(answer)[0];
					return acc;
				}, {});
			}
		}
		return data;
	},
});

export default Serializer;
